import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useQuery, useMutation } from '@apollo/client';
import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  TrashIcon,
  EyeIcon,
  TagIcon,
  CalendarIcon,
  DocumentIcon,
} from '@heroicons/react/24/solid';
import { formatDistanceToNow } from 'date-fns';

const LIST_TRAINING_DOCUMENTS = gql`
  query ListTrainingDocuments($data: ListTrainingDocumentsInput!) {
    listTrainingDocuments(data: $data) {
      documents {
        _id
        name
        url
        type
        size
        format
        created_at
        memoryEntriesCount
        tags
      }
      next_cursor
      total
    }
  }
`;

const DELETE_TRAINING_DOCUMENT = gql`
  mutation DeleteTrainingDocument($data: DeleteTrainingDocumentInput!) {
    deleteTrainingDocument(data: $data) {
      success
      deletedMemoryEntries
      message
    }
  }
`;

interface TrainingDocument {
  _id: string;
  name: string;
  url: string;
  type: string;
  size?: number;
  format?: string;
  created_at: string;
  memoryEntriesCount: number;
  tags: string[];
}

interface TrainingDocumentsProps {
  agentId: string;
  onRefresh?: () => void;
}

export const TrainingDocuments = ({
  agentId,
  onRefresh,
}: TrainingDocumentsProps) => {
  const [t] = useTranslation('training_documents');
  const [selectedDocument, setSelectedDocument] =
    useState<TrainingDocument | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(
    null,
  );

  const { data, loading, error, refetch } = useQuery(LIST_TRAINING_DOCUMENTS, {
    variables: {
      data: {
        agent_id: agentId,
        limit: 20,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const [deleteDocument, { loading: deleteLoading }] = useMutation(
    DELETE_TRAINING_DOCUMENT,
    {
      onCompleted: (data) => {
        if (data.deleteTrainingDocument.success) {
          refetch();
          onRefresh?.();
          setShowDeleteConfirm(null);
        }
      },
      onError: (error) => {
        console.error('Delete document error:', error);
      },
    },
  );

  const handleDeleteDocument = async (documentId: string) => {
    try {
      await deleteDocument({
        variables: {
          data: {
            agent_id: agentId,
            document_id: documentId,
          },
        },
      });
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return t('unknown_size', 'Unknown size');
    const sizes = [
      t('bytes', 'Bytes'),
      t('kb', 'KB'),
      t('mb', 'MB'),
      t('gb', 'GB'),
    ];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (format?: string) => {
    if (!format) return DocumentIcon;
    if (format.includes('pdf')) return DocumentTextIcon;
    return DocumentIcon;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span>
          {t('error_loading', 'Error loading training documents')}:{' '}
          {error.message}
        </span>
      </div>
    );
  }

  const documents = data?.listTrainingDocuments?.documents || [];
  const total = data?.listTrainingDocuments?.total || 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold">
            {t('training_documents', 'Training Documents')}
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            {t(
              'documents_count',
              '{{count}} document(s) uploaded for training',
              { count: total },
            )}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="btn btn-outline btn-sm"
          disabled={loading}
        >
          {loading ? (
            <div className="loading loading-spinner loading-xs"></div>
          ) : (
            t('refresh', 'Refresh')
          )}
        </button>
      </div>

      {/* Documents List */}
      {documents.length === 0 ? (
        <div className="py-12 text-center">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t('no_documents', 'No training documents')}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t(
              'upload_documents_hint',
              'Upload documents to start training your agent',
            )}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {documents.map((document: TrainingDocument) => {
            const FileIcon = getFileIcon(document.format);
            return (
              <motion.div
                key={document._id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="card border border-gray-200 bg-base-100 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="card-body p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex flex-1 items-start space-x-3">
                      <div className="flex-shrink-0">
                        <FileIcon className="h-8 w-8 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="truncate text-sm font-medium text-gray-900">
                          {document.name}
                        </h4>
                        <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
                          <span className="flex items-center">
                            <CalendarIcon className="mr-1 h-3 w-3" />
                            {formatDistanceToNow(
                              new Date(document.created_at),
                              { addSuffix: true },
                            )}
                          </span>
                          <span>{formatFileSize(document.size)}</span>
                          <span className="flex items-center">
                            <DocumentTextIcon className="mr-1 h-3 w-3" />
                            {document.memoryEntriesCount}{' '}
                            {t('memory_entries', 'memory entries')}
                          </span>
                        </div>
                        {document.tags.length > 0 && (
                          <div className="mt-2 flex items-center">
                            <TagIcon className="mr-1 h-3 w-3 text-gray-400" />
                            <div className="flex flex-wrap gap-1">
                              {document.tags.map(
                                (tag: string, index: number) => (
                                  <span
                                    key={index}
                                    className="badge badge-outline badge-xs text-gray-900"
                                  >
                                    {tag}
                                  </span>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 flex items-center space-x-2">
                      <button
                        onClick={() => window.open(document.url, '_blank')}
                        className="btn btn-ghost btn-xs"
                        title={t('view_document', 'View document')}
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(document._id)}
                        className="btn btn-ghost btn-xs text-error hover:bg-error hover:text-white"
                        title={t('delete_document', 'Delete document')}
                        disabled={deleteLoading}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-open modal">
          <div className="modal-box">
            <h3 className="text-lg font-bold">
              {t('confirm_delete', 'Confirm Delete')}
            </h3>
            <p className="py-4">
              {t(
                'delete_warning',
                'Are you sure you want to delete this document? This will also delete all associated memory entries and cannot be undone.',
              )}
            </p>
            <div className="modal-action">
              <button
                className="btn btn-ghost"
                onClick={() => setShowDeleteConfirm(null)}
                disabled={deleteLoading}
              >
                {t('cancel', 'Cancel')}
              </button>
              <button
                className="btn btn-error"
                onClick={() => handleDeleteDocument(showDeleteConfirm)}
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <div className="loading loading-spinner loading-xs"></div>
                ) : (
                  t('delete', 'Delete')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};
